// Armado de planillas (funciones puras, testeables sin navegador ni servidor).
import { PAYMENT_METHODS, formatQty, type PaymentMethod } from '@pos/core';
import type { SaleRow } from '@pos/local-db';

export type Sheet = { name: string; rows: Array<Record<string, unknown>> };

const pesos = (cents: unknown) => (typeof cents === 'number' || typeof cents === 'string' ? Number(cents) / 100 : null);
const methodLabel = (m: string) => PAYMENT_METHODS[m as PaymentMethod]?.label ?? m;

/** Ventas del período en tres hojas: ventas, productos vendidos y pagos. Importes en pesos. */
export function salesSheets(sales: SaleRow[], names: { person: (id: string) => string; customer: (id: string | null | undefined) => string }, timeZone: string): Sheet[] {
  const dt = new Intl.DateTimeFormat('es-AR', { timeZone, dateStyle: 'short', timeStyle: 'short', hourCycle: 'h23' });
  const sorted = [...sales].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  const sign = (s: SaleRow) => (s.kind === 'return' ? -1 : 1);
  return [
    {
      name: 'Ventas',
      rows: sorted.map((s) => ({
        Fecha: dt.format(new Date(s.occurred_at)),
        Tipo: s.kind === 'return' ? 'Devolución' : 'Venta',
        Número: s.number,
        Vendedor: names.person(s.seller_id),
        Cliente: names.customer(s.customer_id),
        Subtotal: pesos(sign(s) * s.subtotal),
        Descuentos: pesos(sign(s) * s.discount_total),
        'Recargo/desc. medio de pago': pesos(sign(s) * (s.adjustment_total + s.rounding_total)),
        IVA: pesos(sign(s) * s.tax_total),
        Total: pesos(sign(s) * s.total),
        'Medios de pago': s.payments.map((p) => methodLabel(p.method)).join(' + '),
      })),
    },
    {
      name: 'Productos vendidos',
      rows: sorted.flatMap((s) =>
        s.items.map((i) => ({
          Fecha: dt.format(new Date(s.occurred_at)),
          Número: s.number,
          Tipo: s.kind === 'return' ? 'Devolución' : 'Venta',
          Producto: i.description,
          Cantidad: (sign(s) * i.qty_milli) / 1000,
          Unidad: formatQty(1000, i.unit_type).replace(/^1\s?/, '') || 'u',
          'Precio unitario': pesos(i.unit_price),
          Descuento: pesos(sign(s) * i.discount),
          'IVA %': i.tax_bps / 100,
          Neto: pesos(sign(s) * i.net),
          IVA: pesos(sign(s) * i.tax),
          Total: pesos(sign(s) * i.total),
        })),
      ),
    },
    {
      name: 'Pagos',
      rows: sorted.flatMap((s) =>
        s.payments.map((p) => ({
          Fecha: dt.format(new Date(s.occurred_at)),
          Número: s.number,
          Medio: methodLabel(p.method),
          'Parte del total': pesos(sign(s) * p.base),
          'Recargo/descuento': pesos(sign(s) * p.adjustment),
          Redondeo: pesos(sign(s) * p.rounding),
          Importe: pesos(sign(s) * p.amount),
          'Pagó con': p.tendered == null ? null : pesos(p.tendered),
        })),
      ),
    },
  ];
}

/** Tablas de la descarga completa (todo lo que el negocio cargó). Los secretos nunca son legibles. */
export const FULL_EXPORT_TABLES: Array<{ table: string; sheet: string; order?: string }> = [
  { table: 'businesses', sheet: 'Negocio' },
  { table: 'memberships', sheet: 'Usuarios' },
  { table: 'devices', sheet: 'Dispositivos' },
  { table: 'categories', sheet: 'Categorías' },
  { table: 'suppliers', sheet: 'Proveedores' },
  { table: 'products', sheet: 'Productos' },
  { table: 'product_variants', sheet: 'Variantes y precios' },
  { table: 'variant_costs', sheet: 'Costos' },
  { table: 'price_history', sheet: 'Historial de precios' },
  { table: 'cost_history', sheet: 'Historial de costos' },
  { table: 'stock_levels', sheet: 'Stock' },
  { table: 'stock_movements', sheet: 'Movimientos de stock', order: 'occurred_at' },
  { table: 'purchases', sheet: 'Compras' },
  { table: 'purchase_items', sheet: 'Productos de compras' },
  { table: 'sales', sheet: 'Ventas', order: 'occurred_at' },
  { table: 'sale_items', sheet: 'Ítems de ventas' },
  { table: 'sale_payments', sheet: 'Pagos de ventas' },
  { table: 'sale_item_costs', sheet: 'Costos de ventas' },
  { table: 'customers', sheet: 'Clientes' },
  { table: 'customer_balances', sheet: 'Saldos de clientes' },
  { table: 'account_entries', sheet: 'Cuenta corriente', order: 'occurred_at' },
  { table: 'registers', sheet: 'Cajas' },
  { table: 'cash_sessions', sheet: 'Turnos de caja', order: 'opened_at' },
  { table: 'cash_movements', sheet: 'Movimientos de caja', order: 'occurred_at' },
  { table: 'audit_log', sheet: 'Auditoría' },
];

